import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDevices, useRevokeDevice } from "@/hooks/useDevices";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/auth";
import { formatRelativeDate } from "../layout/NoteList";
import { Icon, type IconId } from "@/components/ui/Icon";
import { useUIStore } from "../../store/ui";
import { SettingsEmpty, SettingsRow, SettingsSection } from "./parts";

function platformLabel(platform: string | null): string {
  switch (platform) {
    case "web":
      return "Web";
    case "ios":
      return "iPhone";
    case "ipados":
      return "iPad";
    case "macos":
      return "Mac";
    case "android":
      return "Android";
    default:
      return "Unknown device";
  }
}

function platformIcon(platform: string | null): IconId {
  switch (platform) {
    case "ios":
    case "android":
      return "smartphone";
    case "ipados":
      return "tablet";
    case "macos":
      return "laptop";
    case "web":
      return "monitor";
    default:
      return "globe";
  }
}

function locationLabel(country: string | null, city: string | null): string {
  if (city && country) return `${city}, ${country}`;
  if (country) return country;
  if (city) return city;
  return "Unknown location";
}

/** Settings → Account → Devices: every signed-in session, revocable. */
export function DevicesSettings() {
  const { t } = useTranslation();
  const { devices, loading, error, refetch } = useDevices({ active: true });
  const revokeDevice = useRevokeDevice();
  const logout = useAuthStore((s) => s.logout);
  const closeSettings = useUIStore((s) => s.closeSettings);
  const navigate = useNavigate();

  const [confirmId, setConfirmId] = useState<string | null>(null);
  const confirmDevice = devices.find((d) => d.id === confirmId) ?? null;

  const handleConfirmRevoke = async () => {
    if (!confirmDevice) return;
    const isCurrent = confirmDevice.current;
    await revokeDevice.mutateAsync(confirmDevice.id, { force: isCurrent });
    setConfirmId(null);
    if (isCurrent) {
      // Revoking THIS device is an explicit "take my notes off it", so it
      // keeps the full wipe rather than the keep-notes default of Log out.
      closeSettings();
      await logout({ keepNotes: false });
      navigate("/login");
      return;
    }
    refetch();
  };

  return (
    <>
      {loading ? (
        <SettingsEmpty loading />
      ) : error === "Offline" ? (
        <SettingsEmpty icon="offline" title={t("devices.offline")} hint={t("devices.offlineHint")} />
      ) : devices.length === 0 ? (
        <SettingsEmpty icon="monitor" title={t("devices.noSessions")} />
      ) : (
        <SettingsSection>
          {devices.map((d) => (
            <SettingsRow
              key={d.id}
              icon={platformIcon(d.platform)}
              label={
                <span className="flex items-center gap-2">
                  {platformLabel(d.platform)}
                  {d.current && (
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-accent bg-accent-bg px-1.5 py-0.5 rounded">
                      {t("devices.thisDevice")}
                    </span>
                  )}
                </span>
              }
              subtitle={
                <>
                  {locationLabel(d.country, d.city)}
                  {" · "}
                  {t("devices.lastUsed", { date: formatRelativeDate(d.last_used_at) })}
                  {" · "}
                  {t("devices.created", { date: formatRelativeDate(d.created_at) })}
                </>
              }
              trailing={
                // The only session is this one: Log out (Account) already
                // covers it.
                d.current && devices.length === 1 ? (
                  <span />
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setConfirmId(d.id)}
                    className="text-muted hover:text-destructive"
                    title={d.current ? t("devices.signOutCurrent") : t("devices.signOut")}
                  >
                    <Icon name="trash" size={14} />
                  </Button>
                )
              }
            />
          ))}
        </SettingsSection>
      )}

      <Dialog
        alert
        open={!!confirmId}
        onOpenChange={(o) => !o && setConfirmId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDevice?.current ? t("devices.confirmCurrentTitle") : t("devices.confirmRevokeTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="px-4 py-4">
            <p className="text-sm text-ink-secondary">
              {confirmDevice?.current
                ? t("devices.confirmCurrentBody")
                : t("devices.confirmRevokeBody")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setConfirmId(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleConfirmRevoke}
              loading={revokeDevice.isPending}
            >
              {confirmDevice?.current ? t("devices.signOut") : t("devices.revoke")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
